// frontend/src/components/Admin/DeviceDetailModal.tsx
// STAB-01 — Detalle completo de un dispositivo + historial visual (línea temporal)
// + acciones: Revocar (T2 existente), Activar (reactivateDevice autorizada A2),
// Reemplazar (2 pasos: código para nuevo → tras validación, revocar anterior).
// Módulo PERMANENTE de administración de dispositivos.
import React, { useEffect, useState } from 'react';

export interface DeviceSummary {
  id: string;
  adminId: string;
  label: string;
  type?: string;
  status: 'active' | 'revoked' | 'replaced';
  createdAt: string;
  lastSeenAt: string | null;
  revokedAt?: string | null;
  revokedReason?: string | null;
  replacedBy?: string | null;
  activeAuthzCount?: number;
  activeAuthzScopes?: any[];
  activeSessionsCount?: number;
}

interface HistoryEvent {
  at: string;
  event: string;
  detail: string;
}

interface DeviceDetailModalProps {
  device: DeviceSummary;
  token: string;
  onClose: () => void;
  onChanged: () => void; // refrescar lista tras acción
  bootstrap?: boolean;   // sesión admin-session (contraseña): oculta acciones de gestión
                         // (device:manage = RULE_DENIED — H1 por política del Policy Engine v3)
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  active: { label: 'Activo', cls: 'bg-green-100 text-green-700' },
  revoked: { label: 'Revocado', cls: 'bg-red-100 text-red-700' },
  replaced: { label: 'Reemplazado', cls: 'bg-blue-100 text-blue-700' },
};

const EVENT_LABEL: Record<string, string> = {
  device_registered: 'Registro del dispositivo',
  device_revoked: 'Revocación',
  device_reactivated: 'Reactivación',
  device_replaced: 'Reemplazo',
  device_replaced_by: 'Sustituido por',
  authz_issued: 'Autorización emitida',
  authz_renewed: 'Autorización renovada',
  authz_revoked: 'Autorización revocada',
  authz_expired: 'Autorización expirada',
  session_established: 'Sesión establecida',
};

function fmt(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'short' });
}

export default function DeviceDetailModal({ device, token, onClose, onChanged, bootstrap = false }: DeviceDetailModalProps) {
  const [tab, setTab] = useState<'estado' | 'historial'>('estado');
  const [history, setHistory] = useState<HistoryEvent[]>([]);
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState<'revoke' | 'reactivate' | 'replace' | null>(null);
  const [reason, setReason] = useState('');
  const [replaceCode, setReplaceCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [dRes, hRes] = await Promise.all([
          fetch(`/api/auth/devices/${device.id}`, { headers: { Authorization: 'Bearer ' + token } }),
          fetch(`/api/auth/devices/${device.id}/history`, { headers: { Authorization: 'Bearer ' + token } }),
        ]);
        const dData = await dRes.json();
        const hData = await hRes.json();
        if (!dRes.ok) throw new Error(dData.error || 'Error al cargar detalle');
        setDetail(dData.device);
        setHistory(hData.events || []);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [device.id, token]);

  const doRevoke = async () => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`/api/auth/devices/${device.id}`, {
        method: 'DELETE',
        headers: { Authorization: 'Bearer ' + token },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al revocar');
      onChanged(); onClose();
    } catch (e: any) { setError(e.message); setBusy(false); }
  };

  const doReactivate = async () => {
    if (!reason.trim()) { setError('El motivo es obligatorio'); return; }
    setBusy(true); setError('');
    try {
      const res = await fetch(`/api/auth/devices/${device.id}/reactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al reactivar');
      onChanged(); onClose();
    } catch (e: any) { setError(e.message); setBusy(false); }
  };

  const doReplace = async () => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`/api/auth/devices/${device.id}/replace-code`, {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al generar código');
      setReplaceCode(data.code);
      setConfirmAction(null);
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  };

  const badge = STATUS_BADGE[device.status] || STATUS_BADGE.active;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl border border-purple-100 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">{device.label || 'Dispositivo'}</h2>
            <span className="text-sm font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{device.id}</span>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3 flex gap-2 border-b border-gray-100">
          {(['estado', 'historial'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                tab === t ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t === 'estado' ? '📊 Estado' : '🕓 Historial'}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-4">{error}</div>}

          {loading ? (
            <p className="text-sm text-gray-500 text-center py-8">Cargando…</p>
          ) : tab === 'estado' ? (
            <div className="space-y-4">
              {/* Información del dispositivo */}
              <div className="grid grid-cols-2 gap-3">
                <InfoItem label="Administrador" value={device.adminId} />
                <InfoItem label="Tipo" value={device.type || '—'} />
                <InfoItem label="Registrado" value={fmt(device.createdAt)} />
                <InfoItem label="Último uso" value={fmt(device.lastSeenAt)} />
                {device.revokedAt && <InfoItem label="Revocado" value={fmt(device.revokedAt)} />}
                {device.revokedReason && <InfoItem label="Motivo" value={device.revokedReason} />}
                {device.replacedBy && <InfoItem label="Reemplazado por" value={device.replacedBy} />}
              </div>

              {/* Authz y sesiones */}
              {detail && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Autorizaciones y sesiones</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Autorizaciones activas: </span>
                      <span className="font-semibold text-gray-900">{(detail.authz || []).filter((a: any) => a.status === 'active').length}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Sesiones activas: </span>
                      <span className="font-semibold text-gray-900">{(detail.sessions || []).length}</span>
                    </div>
                  </div>
                  {(detail.authz || []).length > 0 && (
                    <div className="mt-3 space-y-1">
                      {(detail.authz || []).slice(0, 5).map((a: any) => (
                        <div key={a.id} className="text-xs text-gray-600 flex justify-between bg-white border border-gray-100 rounded-lg px-3 py-1.5">
                          <span className="font-mono">{a.id}</span>
                          <span>{a.scope?.cartId ? `carrito ${a.scope.cartId}` : 'workspace'}</span>
                          <span className={a.status === 'active' ? 'text-green-600 font-medium' : 'text-red-500'}>{a.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Acciones — ocultas en bootstrap (device:manage exige device-status) */}
              {!bootstrap && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {device.status === 'active' && (
                    <>
                      <button
                        onClick={() => setConfirmAction('revoke')}
                        className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors font-medium"
                      >
                        🔴 Revocar
                      </button>
                      <button
                        onClick={() => setConfirmAction('replace')}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        🔄 Reemplazar
                      </button>
                    </>
                  )}
                  {device.status === 'revoked' && (
                    <button
                      onClick={() => setConfirmAction('reactivate')}
                      className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                      ✅ Activar
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Historial — línea temporal visual */
            <div className="space-y-0">
              {history.length === 0 && <p className="text-sm text-gray-500 text-center py-8">Sin eventos registrados</p>}
              {history.map((ev, i) => (
                <div key={i} className="flex gap-3 relative pb-5">
                  {i < history.length - 1 && <div className="absolute left-[5px] top-4 bottom-0 w-px bg-gray-200" />}
                  <div className="w-3 h-3 rounded-full bg-purple-500 mt-1.5 shrink-0 relative z-10" />
                  <div>
                    <p className="text-xs text-gray-400">{fmt(ev.at)}</p>
                    <p className="text-sm font-medium text-gray-800">{EVENT_LABEL[ev.event] || ev.event}</p>
                    {ev.detail && <p className="text-xs text-gray-500">{ev.detail}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Confirmaciones */}
          {confirmAction === 'revoke' && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-red-800 mb-2">¿Revocar {device.id}?</p>
              <p className="text-xs text-red-700 mb-3">El dispositivo perderá el acceso inmediatamente (autorizaciones revocadas y sesiones invalidadas en vivo).</p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setConfirmAction(null)} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg text-gray-700">Cancelar</button>
                <button onClick={doRevoke} disabled={busy} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg disabled:opacity-50">
                  {busy ? '…' : 'Sí, revocar'}
                </button>
              </div>
            </div>
          )}

          {confirmAction === 'reactivate' && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-green-800 mb-2">¿Reactivar {device.id}?</p>
              <input
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Motivo de la reactivación (obligatorio)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3 focus:ring-2 focus:ring-green-400 outline-none"
              />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setConfirmAction(null)} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg text-gray-700">Cancelar</button>
                <button onClick={doReactivate} disabled={busy || !reason.trim()} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg disabled:opacity-50">
                  {busy ? '…' : 'Sí, reactivar'}
                </button>
              </div>
            </div>
          )}

          {confirmAction === 'replace' && !replaceCode && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-blue-800 mb-2">Reemplazar {device.id} — Paso 1 de 2</p>
              <p className="text-xs text-blue-700 mb-3">
                Se generará un código para registrar el dispositivo NUEVO. Una vez que el nuevo dispositivo se valide correctamente,
                podrás revocar este automáticamente (paso 2).
              </p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setConfirmAction(null)} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg text-gray-700">Cancelar</button>
                <button onClick={doReplace} disabled={busy} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50">
                  {busy ? '…' : 'Generar código'}
                </button>
              </div>
            </div>
          )}

          {replaceCode && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
              <p className="text-sm font-semibold text-blue-800 mb-1">Código para el dispositivo NUEVO</p>
              <div className="text-4xl font-mono font-bold tracking-[0.3em] text-blue-700 bg-white border border-blue-300 rounded-xl py-3 mb-2">
                {replaceCode}
              </div>
              <p className="text-xs text-blue-700 mb-3">
                Regístralo en el dispositivo nuevo. Cuando se valide, vuelve aquí y usa «Revocar» sobre {device.id} (paso 2).
              </p>
              <button onClick={() => setReplaceCode(null)} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg">Cerrar código</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900 break-words">{value}</p>
    </div>
  );
}
